import fetch from 'isomorphic-fetch';
import P from '../pages';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Redirector from '../routes/Redirector'
import FormHeader from './FormHeader';
import { CustomFormGroup, FormDate, CriticalContract } from '../../../shared/components';
import { Checkbox, FormGroup, FormControl, ControlLabel, Button } from 'react-bootstrap';
import GenerateDetailsButton from './GenerateDetailsButton'
import ReactOnRails from 'react-on-rails';
import ReactMarkdown from 'react-markdown';
import TrackableButton from './TrackableButton'
import { setElementValue, randomChars, linkRenderer, formatDate } from '../../../shared/utilities';
import { validator, pageValid } from '../../../shared/validator';
import withDefaults from './withDefaults'
import { registerUser, backToAddress } from '../actions';
import { defaultMessages } from '../../../libs/i18n/default';
import { get } from 'lodash';

import RegistrationInstitution from './RegistrationInstitution'

const FirebaseAccountDetailsForm = ({ page, content, intl, intl: { formatMessage }, territory }) => {
  const dispatch = useDispatch()
  const environment = useSelector(state => state.environment)
  const authenticatedUser = useSelector(state => state.authenticatedUser)
  const recaptchaSiteId = useSelector(state => state.recaptchaSiteId)
  const [acceptedContracts, setAcceptedContracts] = useState([])
  const localProfileElements = useSelector(state => state.localProfileElements)
  const address = useSelector(state => state.address) || {}
  const weChatActive = get(localProfileElements, 'schema.contact.contact_details.wechat_id', false)
  const { guest_types, hide_recaptcha } = territory
  const recaptchaEnabled = recaptchaSiteId && (hide_recaptcha != true)
  const ethnicityOptions = useSelector(state => state.ethnicityOptions)
  const referral = useSelector(state => state.referral)
  const guestTypeOptions = guest_types.map(t => ([t.id, `${t.name}: ${t.guest_blurb}`]))
  const [timers, setTimers] = useState([])
  const [place, setPlace] = useState({ validationKey: 'attributes_place_id', profileElement: true })
  const [affiliate, setAffiliate] = useState(useSelector(state => state.affiliate))
  const [passedRecaptcha, setPassedRecaptcha] = useState(recaptchaEnabled ? false : true)
  const [pressed, setPressed] = useState(false)
  const [placeUnknown, setPlaceUnknown] = useState(false)
  const [captchaBlocked, setCaptchaBlocked] = useState(recaptchaEnabled ? null : false)

  const [user, setUser] = useState({
    givenNames: { validationKey: true, profileElement: true },
    familyName: { validationKey: true, profileElement: true },
    phone: { validationKey: 'attributes_phone', profileElement: true },
    dob: {
      validationKey: 'attributes_dob',
      errors: {
        lessThanMimimum: formatMessage(defaultMessages.clientGuestPagesRegisterPopoversDobTooOld),
        greaterThanMaximum: formatMessage(defaultMessages.clientGuestPagesRegisterPopoversDobTooYoung)
      },
      profileElement: true
    },
    gender: { validationKey: 'attributes_gender', profileElement: true },
    intendedStart: {
      validationKey: 'profile_elements_placement_intended_start',
      profileElement: true,
      errors: {
        lessThanMimimum: formatMessage(defaultMessages.clientGuestPagesRegisterPopoversFutureDate),
        afterMaxDate: formatMessage(defaultMessages.clientGuestPagesRegisterPopoversMaxDate)
      }
    },
    intendedVacate: {
      validationKey: 'profile_elements_placement_intended_vacate',
      profileElement: true,
      errors: {
        lessThanMimimum: formatMessage(defaultMessages.clientGuestPagesRegisterPopoversFutureDate),
        afterMaxDate: formatMessage(defaultMessages.clientGuestPagesRegisterPopoversMaxDate)
      }
    },
    nationality: { validationKey: true, profileElement: true },
    referredFrom: { validationKey: 'attributes_referred_from', profileElement: true },
    guestType: {
      val: territory.guest_types.length > 1 ? '' : territory.guest_types[0].id,
      validationKey: territory.guest_types.length > 1,
      profileElement: territory.guest_types.length > 1 ? true : ''
    },
    weChatId: { validationKey: 'profile_elements_contact_contact_details_wechat_id', profileElement: true }
  })
  const userRef = useRef(user)
  userRef.current = user

  const handleUserChange = async (key, val) => {
    let obj = user[key]
    let result = null
    if (key == 'intendedVacate' || key == 'intendedStart') {
      result = await validator(val, obj.validationKey, key == 'intendedStart' ? user.intendedVacate : user.intendedStart)
    } else {
      result = await validator(val, obj.validationKey)
    }

    const linkedFields = get(result, 'linkedFields')
    obj.valid = get(result, 'valid')
    obj.failedValidation = get(result, 'failedValidation')
    obj.val = val
    obj.loading = false

    if (linkedFields) {
      if (key == 'intendedVacate') {
        setUser({
          ...userRef.current,
          [key]: obj,
          intendedStart: linkedFields
        })
      } else {
        setUser({
          ...userRef.current,
          [key]: obj,
          intendedVacate: linkedFields
        })
      }
    } else {
      setUser({ ...userRef.current, [key]: obj })
    }
  }

  const handlePlaceSearch = async newPlace => {
    let result = await validator(newPlace.value, 'attributes_place_id')

    setPlace({
      ...place,
      val: newPlace.label,
      id: newPlace.value,
      google: newPlace.google,
      valid: String(newPlace.value) === '-1' ? false : get(result, 'valid')
    })
  }

  const handleCitySearch = async newPlace => {
    let result = await validator(newPlace.value, 'attributes_place_id')
    let placeDetails = await $.get("/cities/details", { id: newPlace.value })

    setPlace({
      ...place,
      val: newPlace.label,
      id: newPlace.value,
      google: newPlace.google,
      coordinates: placeDetails.coordinates,
      state: placeDetails.state,
      postcode: placeDetails.postcode,
      valid: get(result, 'valid')
    })
  }

  const invalidatePlace = () => {
    setPlace({ ...place, val: '', valid: null, id: '', google: '' })
  }

  const handleDobChange = (date) => {
    handleUserChange('dob', date)
  }

  const handleIntendedStartChange = (date) => {
    handleUserChange('intendedStart', date)
  }

  const selectOptions = (options) => {
    if (options.sort()) {
      const mo = [<option value="" key="select">{formatMessage(defaultMessages.clientCommonSelectsSelect)}</option>];
      options.forEach(function (option) {
        let value = typeof option === 'string' ? option : option[0]
        let display = typeof option === 'string' ? option : option[1]
        mo.push(<option value={value} key={value}>{display}</option>);
      });
      return mo;
    }
  }

  useEffect(() => {
    setReferralData()
    if (address.value) {
      handlePlaceSearch(address)
      $('#placeSearch').val(address.label)
    }
    $('#placeSearch').autocomplete({
      source: placeUnknown ? "/cities/autocomplete" : "/places/autocomplete_with_google?kind=education_provider",
      minLength: 2,
      autoFocus: true,
      delay: 100,
      select: function (e, ui) {
        e.preventDefault();
        if (!ui || !ui.item) return;
        $('#placeSearch').blur();
        $('#placeSearch').val(ui.item.label);
        placeUnknown ? handleCitySearch(ui.item) : handlePlaceSearch(ui.item)
      },
      change: function (e, ui) {
        if (!ui.item) {
          $('#placeSearch').val('');
          invalidatePlace();
        }
      },
      focus: function (e, ui) {
        e.preventDefault();
      }
    })
  }, [placeUnknown])

  useEffect(() => {
    if (!recaptchaEnabled) return;

    const checkCaptcha = () => {
      fetch('https://newassets.hcaptcha.com/captcha/v1/c3d9cf0/hcaptcha.js', {
        method: 'GET',
      })
        .then(() => setCaptchaBlocked(false))
        .catch(() => setCaptchaBlocked(true))
    }

    checkCaptcha()
  }, [recaptchaSiteId])

  const allContractsSigned = () => (
    territory.guest_critical_contracts.filter(c => !acceptedContracts.includes(c.id)).length == 0
  )

  const generateRandomGuestDetails = () => {
    let randomID = randomChars(4);
    let now = new Date()
    let intendedStart = new Date(now.getFullYear(), (now.getMonth() + 3), now.getDate(), 0, 0, 0)
    let schoolName = territory.country_code == 'US' ? 'University of California, Los Angeles' : 'QUT'
    setElementValue(document.getElementById('givenNames'), 'Test')
      .then(() => setElementValue(document.getElementById('familyName'), `Guest ${randomID}`))
      .then(() => setElementValue(document.getElementById('phone'), `0400111222`))
      .then(() => setElementValue(document.getElementById('weChatId'), `guest${randomID}`))
      .then(() => setElementValue(document.getElementById('gender'), 'Male', 'change'))
      .then(() => setElementValue(document.getElementById('dob_month'), '01', 'change'))
      .then(() => setElementValue(document.getElementById('dob_day'), '01'))
      .then(() => setElementValue(document.getElementById('dob_year'), '2010'))
      .then(() => setElementValue(document.getElementById('intendedStart_month'), ("000" + intendedStart.getMonth()).slice(-2), 'change'))
      .then(() => setElementValue(document.getElementById('intendedStart_day'), intendedStart.getDate()))
      .then(() => setElementValue(document.getElementById('intendedStart_year'), intendedStart.getFullYear()))
      .then(() => setElementValue(document.getElementById('nationality'), 'Australia', 'change'))
      .then(() => setElementValue(document.getElementById('referredFrom'), 'Social Media', 'change'))
      .then(() => {
        if (!place.val || place.val == '') { setElementValue(document.getElementById('placeSearch'), schoolName) }
      })
      .then(() => setElementValue(document.getElementById('guestType'), territory.guest_types[0].id, 'change'))
  }

  const setReferralData = () => {
    if (!referral.referral_uid) { return; }
    setElementValue(document.getElementById('givenNames'), get(referral, 'given_names', ''))
      .then(() => setElementValue(document.getElementById('familyName'), get(referral, 'family_name', '')))
      .then(() => setElementValue(document.getElementById('email'), get(referral, 'email', '')))
      .then(() => setElementValue(document.getElementById('gender'), get(referral, 'gender', ''), 'change'))
      .then(() => setElementValue(document.getElementById('dob_month'), get(referral, 'dob', [])[1], 'change'))
      .then(() => setElementValue(document.getElementById('dob_day'), get(referral, 'dob', [])[2]))
      .then(() => setElementValue(document.getElementById('dob_year'), get(referral, 'dob', [])[0]))
      .then(() => setElementValue(document.getElementById('intendedStart_month'), get(referral, 'intended_start', [])[1], 'change'))
      .then(() => setElementValue(document.getElementById('intendedStart_day'), get(referral, 'intended_start', [])[2]))
      .then(() => setElementValue(document.getElementById('intendedStart_year'), get(referral, 'intended_start', [])[0]))
      .then(() => setElementValue(document.getElementById('nationality'), get(referral, 'nationality', ''), 'change'))
      .then(() => setAffiliate(referral.affiliate_code))
  }

  const completeFieldsPrompt = () => (
    pageValid({ ...user, place }) ? 'hidden' : 'visible'
  )

  const textOrIcon = () => (
    pressed
      ? <i className="fa fa-fw fa-spinner fa-spin" />
      : formatMessage(defaultMessages.clientCommonButtonsSignup)
  )

  const formValid = () => (
    allContractsSigned() &&
    (pageValid({ ...user, place }) || (pageValid(user) && placeUnknown)) &&
    passedRecaptcha
  )

  const submit = () => {
    setPressed(true)
    let userObj = {}
    Object.entries(user).forEach(([key, val]) => {
      Object.assign(userObj, { [key]: val.val })
    });

    let payload = {
      user: userObj,
      firebase_id: authenticatedUser.firebase_id,
      affiliate: affiliate || authenticatedUser.affiliate_code,
    }

    if (placeUnknown) {
      payload.city = { id: place.id, google: place.google, coordinates: place.coordinates, name: place.val, state: place.state }
    } else {
      payload.place = { id: place.id, google: place.google }
    }

    if (authenticatedUser) {
      payload.user.email = authenticatedUser.email
    }

    dispatch(registerUser(payload))
  }

  const updateAcceptedContracts = (contract, accepted) => {
    let newAcceptedContracts = acceptedContracts.slice(0)
    if (accepted) {
      if (!newAcceptedContracts.includes(contract.id)) {
        newAcceptedContracts.push(contract.id)
      }
    } else {
      newAcceptedContracts = newAcceptedContracts.filter(ac => ac != contract.id)
    }
    setAcceptedContracts(newAcceptedContracts)
  }

  const intendedStartWithinTwoWeeks = () => {
    if (!user.intendedStart || !user.intendedStart.val) { return false }
    const fortnightAway = new Date(Date.now() + 12096e5) // 14 days in milliseconds
    const intendedStart = new Date(user.intendedStart.val)
    const yesterday = new Date(Date.now() - 864e5) // 1 day in milliseconds

    return intendedStart < fortnightAway && intendedStart > yesterday
  }

  const goBack = () => {
    let params = new URLSearchParams(window.location.search)
    params.delete('place_id')
    params.delete('place_name')
    params.delete('place_from_google')
    window.history.replaceState({}, document.title, `/guests/signup${params.toString() ? `?${params.toString()}` : ''}#/register`)
    dispatch(backToAddress())
  }

  const updatePlaceUnknown = () => {
    if (placeUnknown) {
      invalidatePlace()
      $('#placeSearch').val('')
    }
    setPlaceUnknown(!placeUnknown)
  }

  return (
    <div className="register_form">
      <Redirector this_page={P.FIREBASE_ACCOUNT_DETAILS_FORM_PAGE} page={page} />
      <FormHeader title={content.pages.firebase_account_details.title} />
      <GenerateDetailsButton
        style={{ textAlign: 'center' }}
        generateDetails={generateRandomGuestDetails}
        text='Generate'
        enabled={territory.generate_details_enabled}
        environment={environment} />
      <CustomFormGroup field={user.givenNames}>
        <ControlLabel>
          {territory.code === 'ASC' ? (
            formatMessage(defaultMessages.clientGuestPagesRegisterLabelsAscGivenName)
          ) : (
            formatMessage(defaultMessages.clientGuestPagesRegisterLabelsGivenName)
          )}
        </ControlLabel>
        <FormControl id="givenNames"
          type="text"
          componentClass="input"
          onChange={e => handleUserChange(e.target.id, e.target.value)} />
      </CustomFormGroup>

      <CustomFormGroup field={user.familyName}>
        <ControlLabel>
          {territory.code === 'ASC' ? (
            formatMessage(defaultMessages.clientGuestPagesRegisterLabelsAscFamilyName)
          ) : (
            formatMessage(defaultMessages.clientGuestPagesRegisterLabelsFamilyName)
          )}
        </ControlLabel>
        <FormControl
          id="familyName"
          type="text"
          componentClass="input"
          onChange={e => handleUserChange(e.target.id, e.target.value)} />
      </CustomFormGroup>

      <CustomFormGroup field={user.phone}>
        <ControlLabel>{formatMessage(defaultMessages.clientCommonPhone)}</ControlLabel>
        <FormControl
          id="phone"
          type="text"
          componentClass="input"
          onChange={e => handleUserChange(e.target.id, e.target.value)} />
      </CustomFormGroup>

      {
        weChatActive &&
        <CustomFormGroup field={user.weChatId}>
          <ControlLabel>{formatMessage(defaultMessages.clientCommonWechat)}</ControlLabel>
          <FormControl
            id="weChatId"
            type="text"
            componentClass="input"
            onChange={e => handleUserChange(e.target.id, e.target.value)} />
        </CustomFormGroup>
      }

      <CustomFormGroup field={user.gender}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesRegisterLabelsGender)}</ControlLabel>
        <FormControl id="gender"
          type="select"
          componentClass="select"
          onChange={e => handleUserChange(e.target.id, e.target.value)} >
          <option value="" key="select">{formatMessage(defaultMessages.clientCommonSelectsSelect)}</option>
          <option value="Male" key="male">{formatMessage(defaultMessages.clientCommonMale)}</option>
          <option value="Female" key="female">{formatMessage(defaultMessages.clientCommonFemale)}</option>
          <option value="Other" key="other">{formatMessage(defaultMessages.clientCommonOtherGender)}</option>
        </FormControl>
      </CustomFormGroup>

      <CustomFormGroup field={user.dob}>
        <ControlLabel>
          {formatMessage(defaultMessages.clientGuestPagesRegisterLabelsDob)}
        </ControlLabel>
        <FormDate
          setDate={date => handleUserChange('dob', date)}
          formDateId={'dob'}
          date={user.dob.val} />
      </CustomFormGroup>

      <CustomFormGroup field={user.intendedStart} >
        <ControlLabel>{content.pages.register.labels.intended_start}</ControlLabel>
        <FormDate
          setDate={date => handleUserChange('intendedStart', date)}
          formDateId={'intendedStart'}
          date={user.intendedStart.val} />
        {
          intendedStartWithinTwoWeeks() &&
          <div className='error-message' style={{ color: '#b77600' }}>
            {formatMessage(defaultMessages.clientGuestPagesRegisterPopoversIntendedStart)}
          </div>
        }
      </CustomFormGroup>

      <CustomFormGroup field={user.intendedVacate} >
        <ControlLabel>{content.pages.register.labels.intended_vacate}</ControlLabel>
        <FormDate
          setDate={date => handleUserChange('intendedVacate', formatDate(date))}
          formDateId={'intendedVacate'}
          date={user.intendedVacate.val} />
      </CustomFormGroup>

      <CustomFormGroup field={user.nationality}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesRegisterLabelsNationality)}</ControlLabel>
        <FormControl id="nationality"
          type="select"
          componentClass="select"
          onChange={e => handleUserChange(e.target.id, e.target.value)} >
          <option value="" key="select">{formatMessage(defaultMessages.clientCommonSelectsSelect)}</option>
          {ethnicityOptions.map((opt, index) => {
            return (
              <option
                key={`${opt[1]}_${index}`}
                value={opt[1]}>{opt[0]}</option>
            );
          })}
        </FormControl>
      </CustomFormGroup>

      {territory.guest_types.length > 1 && (
        <CustomFormGroup field={user.guestType}>
          <ControlLabel>{content.pages.register.labels.education_institution}</ControlLabel>
          <FormControl id="guestType"
            type="select"
            componentClass="select"
            onChange={e => handleUserChange(e.target.id, e.target.value)} >
            {selectOptions(guestTypeOptions)}
          </FormControl>
        </CustomFormGroup>
      )}

      <RegistrationInstitution
        content={content}
        place={place}
        territory={territory}
        invalidatePlace={invalidatePlace}
        formatMessage={formatMessage}
        placeUnknown={placeUnknown}
        setPlaceUnknown={setPlaceUnknown}
        handlePlaceSearch={handlePlaceSearch}
      />

      <CustomFormGroup field={user.referredFrom}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesRegisterLabelsReferredFrom)}</ControlLabel>
        <FormControl id="referredFrom"
          type="select"
          componentClass="select"
          onChange={e => handleUserChange(e.target.id, e.target.value)} >
          {selectOptions(territory.guest_marketing_referrers)}
        </FormControl>
      </CustomFormGroup>

      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 10 }}>
          {territory.guest_critical_contracts.map((c, i) => (
            <CriticalContract
              contract={c}
              key={`contract_${i}`}
              contractAccepted={(accepted) => updateAcceptedContracts(c, accepted)} />
          ))}
        </div>

        {recaptchaEnabled && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <HCaptcha
              sitekey={recaptchaSiteId}
              onVerify={(token, ekey) => { setPassedRecaptcha(true) }}
            />
          </div>
        )}

        {captchaBlocked && (
          <div className="alert alert-danger">
            <p>Failed to load recaptcha. Your privacy settings may be too high. Please adjust your browser security and try again.</p>
          </div>
        )}

        {
          address.value &&
          <Button
            type="submit"
            id='back_btn'
            style={{ float: 'left' }}
            onClick={goBack}
            className="btn btn-warning btn-lg">Back</Button>
        }
        <TrackableButton
          type="submit"
          className="btn btn-primary btn-lg"
          id="sign_up_btn"
          page={page}
          user={user}
          style={{ width: '107px', float: address.value && 'right' }}
          disabled={!formValid() || pressed}
          text={textOrIcon()}
          onClick={submit} />
        <p style={{ visibility: completeFieldsPrompt(), margin: 0, color: 'red' }}>{formatMessage(defaultMessages.clientGuestPagesRegisterLabelsCompleteFields)}</p>
      </div>
    </div>
  );
}

export default withDefaults(FirebaseAccountDetailsForm);

